import React, { createContext, useState } from 'react'

import useQuiz from 'components/ControlBar2.0/TopControlBar/LeftControls/components/Quiz/useQuiz'
import useScreenShareToggle from 'components/ControlBar2.0/TopControlBar/LeftControls/components/Screenshare/useScreenshare'
import useWhiteboard from 'components/ControlBar2.0/TopControlBar/LeftControls/components/Whiteboard/useWhiteboard'
import useIframeShare from 'hooks/useIframeShare'
import { getIdentity } from 'hooks/useParticipants/useParticipants'

import AttachVisibilityHandler from './AttachVisibilityHandler/AttachVisibilityHandler'
import useHandleOnDisconnect from './useHandleOnDisconnect/useHandleOnDisconnect'
import useHandleRoomDisconnectionErrors from './useHandleRoomDisconnectionErrors/useHandleRoomDisconnectionErrors'
import useHandleTrackPublicationFailed from './useHandleTrackPublicationFailed/useHandleTrackPublicationFailed'
import useLocalTracks from './useLocalTracks/useLocalTracks'
import useRoom from './useRoom/useRoom'
import { SelectedParticipantProvider } from './useSelectedParticipant/useSelectedParticipant'
/*
 *  The hooks used by the VideoProvider component are different than the hooks found in the 'hooks/' directory. The hooks
 *  in the 'hooks/' directory can be used anywhere in a video application, and they can be used any number of times.
 *  the hooks in the 'VideoProvider/' directory are intended to be used by the VideoProvider component only. Using these hooks
 *  elsewhere in the application may cause problems as these hooks should not be used more than once in an application.
 */

export const VideoContext = createContext(null)

export function VideoProvider({
    options,
    children,
    onError = () => {},
    onDisconnect = () => {},
    token,
    userType,
    roomName,
    userId,
    roomMode,
    userToken,
}) {
    const onErrorCallback = error => onError(error)

    const {
        audioTrack,
        videoTrack,
        dataTrack,
        localTracks,
        getLocalVideoTrack,
        getLocalAudioTrack,
        isAcquiringLocalTracks,
    } = useLocalTracks()
    const { room, isConnecting, connect } = useRoom(
        localTracks,
        onErrorCallback,
        options
    )
    const [isWhiteboardMode, setWhiteboardMode] = useState(false)
    const [isArtefactMode, setArtefactMode] = useState(false)
    const [isQuizMode, setQuizMode] = useState(false)
    const [isScreenShareMode, setScreenShareMode] = useState(false) // for w/b and try to load doc in iframe
    const [remoteDataPub, setRemoteDataPub] = useState(null)
    const [roomModeState, setRoomModeState] = useState(roomMode)
    const [featuresAllowed, setFeaturesAllowed] = useState([])
    const [isDefaultScreenShare, toggleDefaultScreenShare] = useState(false) //  Default screenshare for custom tab or window
    const [isAnyoneShareScreen, toggleAnyoneScreenShare] = useState(false) // check if anyone present screen or not
    const [isTeacherPresent, toggleTeacherPresent] = useState(false)
    // Websocket messages
    const [msg, setMsg] = useState(null)
    const [chatMsg, setChatMsg] = useState(null)
    const [activeMsg, setActiveMsg] = useState(null)

    const [isReconnecting, setIsReconnecting] = useState(false)

    // Register onError and onDisconnect callback functions.
    useHandleRoomDisconnectionErrors(room, onError)
    useHandleTrackPublicationFailed(room, onError)
    useHandleOnDisconnect(room, onDisconnect)

    /* Sentry user identification setup */
    const identity = getIdentity(room?.localParticipant?.identity)

    console.count('VideoProvider component')

    const screenShare = useScreenShareToggle({
        isArtefactMode,
        isDefaultScreenShare,
        isAnyoneShareScreen,
        room,
        onError,
        toggleDefaultScreenShare,
        userId,
    })

    const quiz = useQuiz({
        isQuizMode,
        isArtefactMode,
        setQuizMode,
        setWhiteboardMode,
        setArtefactMode,
    })

    const whiteboard = useWhiteboard({
        setWhiteboardMode,
        isWhiteboardMode,
        isArtefactMode,
    })

    const jeopardy = useIframeShare({
        isArtefactMode,
    })

    //TO-DO restructure context
    return (
        <VideoContext.Provider
            value={{
                room,
                connect,
                isConnecting,
                onError: onErrorCallback,
                onDisconnect,

                /* local tracks */
                localTracks,
                audioTrack,
                videoTrack,
                dataTrack,
                getLocalVideoTrack,
                getLocalAudioTrack,
                isAcquiringLocalTracks,

                token,
                userType,
                roomName,
                userId,
                userToken,
                roomModeState,
                setRoomModeState,
                featuresAllowed,
                setFeaturesAllowed,
                isReconnecting,
                setIsReconnecting,
                isWhiteboardMode,
                setWhiteboardMode,
                isQuizMode,
                setQuizMode,
                remoteDataPub,
                setRemoteDataPub,
                chatMsg,
                setChatMsg,
                activeMsg,
                setActiveMsg,
                isScreenShareMode,
                setScreenShareMode,
                msg,
                setMsg,
                isDefaultScreenShare,
                toggleDefaultScreenShare,
                isAnyoneShareScreen,
                toggleAnyoneScreenShare,
                isTeacherPresent,
                toggleTeacherPresent,
                isArtefactMode,
                setArtefactMode,
                isGhost: identity?.isGhost,
                screenShare,
                quiz,
                whiteboard,
                jeopardy,
            }}
        >
            <SelectedParticipantProvider room={room}>
                {children}
            </SelectedParticipantProvider>
            {/*
        The AttachVisibilityHandler component is using the useLocalVideoToggle hook
        which must be used within the VideoContext Provider.
      */}
            <AttachVisibilityHandler />
        </VideoContext.Provider>
    )
}
